import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box, Chip, Typography, Pagination, Stack, Grid,
} from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';
import { Button, Card, Select, PageHeader } from 'Components/ui';
import { useTranslation } from 'react-i18next';
import { getScannerScan, getScanFindings } from 'services/scanner-service';
import { Main as Container } from 'Components/Main';

const STATUS_COLORS = {
  executing: 'warning',
  available: 'success',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
};
const SEVERITY_COLORS = {
  critical: 'error', high: 'warning', medium: 'default', low: 'default', informational: 'default',
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handle} title="Copiar ARN" style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
      color: copied ? 'var(--vo-success)' : 'var(--vo-steel)', display: 'inline-flex', alignItems: 'center',
      transition: 'color 0.15s', verticalAlign: 'middle',
    }}>
      {copied
        ? <Check style={{ fontSize: 11 }} />
        : <ContentCopy style={{ fontSize: 11 }} />
      }
    </button>
  );
};

const BTN = {
  borderRadius: '8px',
  padding: '8px 20px',
  height: '36px',
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
};

export const ScannerScanShow = () => {
  const { t } = useTranslation('scanner');
  const { id } = useParams();
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [findingsPage, setFindingsPage] = useState(1);

  const STATUS_LABELS = {
    executing: t('scanner:scanStatus.executing'),
    available: t('scanner:scanStatus.available'),
    completed: t('scanner:scanStatus.completed'),
    failed: t('scanner:scanStatus.failed'),
    cancelled: t('scanner:scanStatus.cancelled'),
  };

  const SEVERITY_LABELS = {
    critical: t('scanner:severity.critical'),
    high: t('scanner:severity.high'),
    medium: t('scanner:severity.medium'),
    low: t('scanner:severity.low'),
    informational: t('scanner:severity.informational_full'),
  };

  const { data: scanData } = useQuery(['scan', id], () => getScannerScan(id), { enabled: !!id });

  const { data: findingsData, isLoading: findingsLoading } = useQuery(
    ['scanFindings', id, findingsPage, severityFilter, statusFilter],
    () => getScanFindings(id, findingsPage, severityFilter, statusFilter),
    { enabled: !!id, keepPreviousData: true },
  );

  const scan = scanData?.data;
  const findings = findingsData?.data?.findings || [];
  const totalPages = findingsData?.data?.pagination?.pages;

  // '' means genuinely no selection; 'all' is the real, explicit "All" menu
  // choice — both mean "don't filter on this field," distinct only in how
  // the Select renders them (muted placeholder vs. full-strength value).
  const isFilterActive = (value) => value && value !== 'all';

  return (
    <Container>
      <PageHeader
        title={t('scanner:scanShow.title')}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('scanner:dashboard.title'), route: '/scanner/dashboard' },
          { label: t('scanner:scans.title'), route: '/scanner/scans' },
          { label: t('scanner:scanShow.title') },
        ]}
      />
      <Box sx={{ mt: 3, pb: 6 }}>

      {/* Scan Info */}
      {scan && (
        <Card elevation={0} sx={{ p: 3, mb: 3, border: '1px solid var(--vo-line)', borderRadius: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--vo-steel)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('scanner:scanShow.labelStatus')}
              </Typography>
              <Box mt={0.5}>
                <Chip
                  label={STATUS_LABELS[scan.state] || scan.state}
                  color={STATUS_COLORS[scan.state] || 'default'}
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--vo-steel)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('scanner:scanShow.labelStartedAt')}
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'var(--vo-ink)', mt: 0.5 }}>
                {scan.started_at ? new Date(scan.started_at).toLocaleString('pt-BR') : '—'}
              </Typography>
            </Grid>
            {scan.completed_at && (
              <Grid item>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--vo-steel)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('scanner:scanShow.labelCompletedAt')}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'var(--vo-ink)', mt: 0.5 }}>
                  {new Date(scan.completed_at).toLocaleString('pt-BR')}
                </Typography>
              </Grid>
            )}
            {scan.duration && (
              <Grid item>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--vo-steel)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('scanner:scanShow.labelDuration')}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'var(--vo-ink)', mt: 0.5 }}>
                  {t('scanner:scanShow.duration', { minutes: Math.round(scan.duration / 60) })}
                </Typography>
              </Grid>
            )}
          </Grid>
          {scan.error_message && (
            <Box mt={2} sx={{ p: 2, backgroundColor: 'var(--vo-danger-10)', border: '1px solid var(--vo-danger-15)', borderRadius: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: 'var(--vo-danger)' }}>{t('scanner:scanShow.errorPrefix')} {scan.error_message}</Typography>
            </Box>
          )}
        </Card>
      )}

      {/* Findings Filters */}
      <Card elevation={0} sx={{ p: 3, mb: 3, border: '1px solid var(--vo-line)', borderRadius: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--vo-ink)', mb: 2 }}>{t('scanner:scanShow.findingsTitle')}</Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <Select
            name="severityFilter"
            value={severityFilter}
            onChange={value => { setSeverityFilter(value); setFindingsPage(1); }}
            fullWidth={false}
            sx={{ minWidth: 160 }}
            options={[
              { value: '', label: t('scanner:scanShow.selectSeverity') },
              { value: 'all', label: t('scanner:scanShow.allSeverities') },
              { value: 'critical', label: t('scanner:severity.critical') },
              { value: 'high', label: t('scanner:severity.high') },
              { value: 'medium', label: t('scanner:severity.medium') },
              { value: 'low', label: t('scanner:severity.low') },
              { value: 'informational', label: t('scanner:severity.informational_full') },
            ]}
          />
          <Select
            name="statusFilter"
            value={statusFilter}
            onChange={value => { setStatusFilter(value); setFindingsPage(1); }}
            fullWidth={false}
            sx={{ minWidth: 140 }}
            options={[
              { value: '', label: t('scanner:scanShow.selectStatus') },
              { value: 'all', label: t('scanner:scanShow.allStatuses') },
              { value: 'FAIL', label: t('scanner:scanShow.failed') },
              { value: 'PASS', label: t('scanner:scanShow.passed') },
              { value: 'MUTED', label: t('scanner:scanShow.muted') },
            ]}
          />
          {(isFilterActive(severityFilter) || isFilterActive(statusFilter)) && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setSeverityFilter(''); setStatusFilter(''); setFindingsPage(1); }}
              sx={{ ...BTN, color: 'var(--vo-slate)', borderColor: 'var(--vo-steel)' }}
            >
              {t('scanner:scanShow.clearFilters')}
            </Button>
          )}
        </Box>
      </Card>

      {/* Findings Table */}
      <Card elevation={0} sx={{ padding: 0, border: '1px solid var(--vo-line)', borderRadius: 2, overflow: 'hidden' }}>
        {findingsLoading && (
          <Box sx={{ p: 6, textAlign: 'center', color: 'var(--vo-slate)' }}>{t('scanner:scanShow.loadingFindings')}</Box>
        )}

        {!findingsLoading && findings.length === 0 && (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <Typography sx={{ color: 'var(--vo-slate)' }}>{t('scanner:scanShow.noFindings')}</Typography>
          </Box>
        )}

        {findings.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--vo-mist)' }}>
                  {[
                    t('scanner:scanShow.colCheck'),
                    t('scanner:scanShow.colResource'),
                    t('scanner:scanShow.colService'),
                    t('scanner:scanShow.colRegion'),
                    t('scanner:scanShow.colSeverity'),
                    t('scanner:scanShow.colStatus'),
                    t('scanner:scanShow.colDetails'),
                  ].map(col => (
                    <th key={col} style={{ padding: '12px 16px', fontWeight: 600, fontSize: 12, color: 'var(--vo-slate)', textAlign: 'left', borderBottom: '1px solid var(--vo-line)', whiteSpace: 'nowrap' }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {findings.map(f => (
                  <tr key={f.id || f.uid} style={{ borderBottom: '1px solid var(--vo-line)' }}>
                    <td style={{ padding: '10px 16px', maxWidth: 260 }}>
                      <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--vo-ink)', lineHeight: 1.4 }}>
                        {f.check_title || f.check_id || '—'}
                      </div>
                      {f.check_title && (
                        <div style={{ fontSize: 11, color: 'var(--vo-steel)', marginTop: 2 }}>{f.check_id}</div>
                      )}
                    </td>
                    <td style={{ padding: '10px 16px', maxWidth: 240, whiteSpace: 'nowrap' }}>
                      {(() => {
                        const arn = f.resource_uid || (f.account_uid && f.resource_name ? `${f.account_uid}/${f.resource_name}` : f.resource_name) || '';
                        const short = arn.length > 28 ? `${arn.slice(0, 26)}…` : arn;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--vo-slate)', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              title={arn}>
                              {short || '—'}
                            </Typography>
                            {arn && <CopyButton text={arn} />}
                          </Box>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <Chip label={f.service_name || '—'} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--vo-slate)', fontFamily: 'monospace' }}>
                      {f.region || '—'}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <Chip
                        label={SEVERITY_LABELS[f.severity] || f.severity}
                        color={SEVERITY_COLORS[f.severity] || 'default'}
                        size="small"
                      />
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <Chip
                        label={f.status}
                        color={f.status === 'FAIL' ? 'error' : f.status === 'PASS' ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </td>
                    <td style={{ padding: '10px 16px', maxWidth: 280, fontSize: 12, color: 'var(--vo-slate)', lineHeight: 1.4 }}>
                      {f.status_extended || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack sx={{ justifyContent: 'center', flexDirection: 'row', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={findingsPage}
            onChange={(_, p) => setFindingsPage(p)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
      </Box>
    </Container>
  );
};
